import { useRef, useState } from 'react'
import { useLoriState } from '../contexts/LoriContext'

// Custom hook to handle Lori voice/audio input
export function useLoriVoice(activeURL: string) {
    const [recording, setRecording] = useState(false)
    const audioContextRef = useRef<AudioContext | null>(null)
    const processorRef = useRef<ScriptProcessorNode | null>(null)
    const streamRef = useRef<MediaStream | null>(null)
    const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
    const desiredSampleRate = 24000
    const audioChunksRef = useRef<Int16Array[]>([])
    const { socket } = useLoriState()

    // Downsamples a Float32Array to Int16Array; optionally changes sample rate
    function downsampleBuffer(
        buffer: Float32Array,
        inputSampleRate: number,
        outputSampleRate: number,
    ): Int16Array {
        if (inputSampleRate === outputSampleRate) {
            const pcm = new Int16Array(buffer.length)
            for (let i = 0; i < buffer.length; i++) {
                const s = Math.max(-1, Math.min(1, buffer[i]))
                pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff
            }
            return pcm
        }
        const sampleRateRatio = inputSampleRate / outputSampleRate
        const newLength = Math.round(buffer.length / sampleRateRatio)
        const result = new Int16Array(newLength)
        let offsetResult = 0
        let offsetBuffer = 0
        while (offsetResult < result.length) {
            const nextOffsetBuffer = Math.round(
                (offsetResult + 1) * sampleRateRatio,
            )
            let accum = 0,
                count = 0
            for (
                let i = offsetBuffer;
                i < nextOffsetBuffer && i < buffer.length;
                i++
            ) {
                accum += buffer[i]
                count++
            }
            let s = accum / count
            s = Math.max(-1, Math.min(1, s))
            result[offsetResult] = s < 0 ? s * 0x8000 : s * 0x7fff
            offsetResult++
            offsetBuffer = nextOffsetBuffer
        }
        return result
    }

    // Start audio recording: sets up refs and streams
    const startRecording = async () => {
        if (recording) return
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
            })
            streamRef.current = stream
            const audioContext = new (window.AudioContext ||
                (window as any).webkitAudioContext)({
                sampleRate: desiredSampleRate,
            })
            audioContextRef.current = audioContext
            const inputSampleRate = audioContext.sampleRate
            const source = audioContext.createMediaStreamSource(stream)
            sourceRef.current = source
            const processor = audioContext.createScriptProcessor(4096, 1, 1)
            processorRef.current = processor
            audioChunksRef.current = []
            source.connect(processor)
            processor.connect(audioContext.destination)
            processor.onaudioprocess = function (e: AudioProcessingEvent) {
                const input = e.inputBuffer.getChannelData(0)
                const pcm = downsampleBuffer(
                    input,
                    inputSampleRate,
                    desiredSampleRate,
                )
                audioChunksRef.current.push(pcm)
            }
            setRecording(true)
        } catch (err) {
            setRecording(false)
        }
    }

    // Stop recording, clean up, and send the audio data via WS
    const stopRecording = () => {
        if (processorRef.current) {
            processorRef.current.disconnect()
            processorRef.current.onaudioprocess = null
            processorRef.current = null
        }
        if (sourceRef.current) {
            sourceRef.current.disconnect()
            sourceRef.current = null
        }
        if (audioContextRef.current) {
            audioContextRef.current.close()
            audioContextRef.current = null
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop())
            streamRef.current = null
        }
        setRecording(false)
        // Send all PCM chunks as a single array
        const allChunks = audioChunksRef.current
        if (
            allChunks.length > 0 &&
            socket &&
            socket.readyState === WebSocket.OPEN
        ) {
            const totalLength = allChunks.reduce(
                (sum, arr) => sum + arr.length,
                0,
            )
            const combined = new Int16Array(totalLength)
            let offset = 0
            for (const chunk of allChunks) {
                combined.set(chunk, offset)
                offset += chunk.length
            }
            // Send as JSON for ease of backend handling
            const audioMessage = {
                type: 'audio',
                audioData: Array.from(combined),
                activeURL,
            }
            socket.send(JSON.stringify(audioMessage))
        }
        audioChunksRef.current = []
    }

    return { recording, startRecording, stopRecording }
}
